// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: voice-mode sound cues. Soft bell tones on a pentatonic scale, made with
// Web Audio (no files): a rising pair when voice mode opens, a lift when your turn
// is heard, a quiet two-note pulse while the Assistant thinks, and small cues for
// interrupt, mute, errors and closing.

export type VoiceModeCue = 'start' | 'heard' | 'thinking' | 'interrupt' | 'mute' | 'unmute' | 'error' | 'end';

interface Note {
	/** Hz at the start of the note. */
	freq: number;
	/** Optional glide target in Hz. */
	glide?: number;
	at: number;
	length: number;
	gain: number;
}

const E5 = 659.25;
const G5 = 783.99;
const A5 = 880;
const B5 = 987.77;
const D6 = 1174.66;
const E6 = 1318.51;
const A4 = 440;
const F4 = 349.23;
const C5 = 523.25;
const D5 = 587.33;

export const VOICE_MODE_CUES: Record<VoiceModeCue, ReadonlyArray<Note>> = {
	start: [
		{freq: E5, at: 0, length: 0.5, gain: 0.9},
		{freq: B5, at: 0.11, length: 0.7, gain: 0.8},
	],
	heard: [{freq: G5, glide: D6, at: 0, length: 0.32, gain: 0.7}],
	thinking: [
		{freq: C5, at: 0, length: 0.45, gain: 0.32},
		{freq: E5, at: 0.22, length: 0.55, gain: 0.26},
	],
	interrupt: [{freq: A5, glide: E5, at: 0, length: 0.22, gain: 0.6}],
	mute: [{freq: G5, glide: D5, at: 0, length: 0.18, gain: 0.5}],
	unmute: [{freq: D5, glide: G5, at: 0, length: 0.18, gain: 0.5}],
	error: [
		{freq: A4, at: 0, length: 0.4, gain: 0.7},
		{freq: F4, at: 0.16, length: 0.6, gain: 0.7},
	],
	end: [
		{freq: E6, at: 0, length: 0.45, gain: 0.55},
		{freq: B5, at: 0.1, length: 0.5, gain: 0.55},
		{freq: E5, at: 0.2, length: 0.8, gain: 0.6},
	],
};

/** How long a cue rings, for callers that wait before closing the audio context. */
export function cueLength(cue: VoiceModeCue): number {
	return Math.max(...VOICE_MODE_CUES[cue].map((note) => note.at + note.length));
}

const MASTER_GAIN = 0.16;
// A bell: the note, a quieter octave, and a faint shimmer above.
const PARTIALS: ReadonlyArray<{ratio: number; gain: number; type: OscillatorType}> = [
	{ratio: 1, gain: 1, type: 'sine'},
	{ratio: 2, gain: 0.28, type: 'sine'},
	{ratio: 3.01, gain: 0.06, type: 'triangle'},
];

export function playVoiceModeCue(
	context: BaseAudioContext,
	cue: VoiceModeCue,
	destination: AudioNode = context.destination,
	at = context.currentTime,
): void {
	const master = context.createGain();
	master.gain.value = MASTER_GAIN;
	master.connect(destination);
	for (const note of VOICE_MODE_CUES[cue]) {
		const start = at + note.at;
		const end = start + note.length;
		const envelope = context.createGain();
		envelope.gain.setValueAtTime(0.0001, start);
		envelope.gain.exponentialRampToValueAtTime(note.gain, start + 0.012);
		envelope.gain.exponentialRampToValueAtTime(0.0001, end);
		envelope.connect(master);
		for (const partial of PARTIALS) {
			const oscillator = context.createOscillator();
			oscillator.type = partial.type;
			oscillator.frequency.setValueAtTime(note.freq * partial.ratio, start);
			if (note.glide)
				oscillator.frequency.exponentialRampToValueAtTime(note.glide * partial.ratio, start + note.length * 0.6);
			const level = context.createGain();
			level.gain.value = partial.gain;
			oscillator.connect(level).connect(envelope);
			oscillator.start(start);
			oscillator.stop(end + 0.02);
		}
	}
}
